import { withAccountConfirmation } from "../account-confirmation";
/**
 * Platform admin (operator) dashboard. Every call needs `admin:platform`, which only users with
 * `is_platform_admin` hold; the /admin route refuses everyone else before any of these run.
 *
 *   GET  /v1/admin/overview                     headline numbers + "needs you" list, polled every 30 s
 *   GET  /v1/admin/nodes[/{node}]               servers; cordon, uncordon, drain (passkey)
 *   GET  /v1/admin/workspaces[/{id}]            tenants; suspend (passkey), unsuspend
 *   GET  /v1/admin/environments/{id}/quota      an environment's resource quota; PATCH to override the plan's
 *   GET  /v1/admin/users[/{id}]                 accounts; disable (passkey), enable
 *   GET  /v1/admin/finance?month=               revenue, costs and margin
 *   GET  /v1/admin/usage/timeseries             platform usage (same shape as workspace usage)
 *   GET  /v1/admin/operations                   builds and deploys across every workspace
 *   plus the existing waitlist, invites, incidents, AI ops changes and kill switch endpoints.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createCredential, getAssertion } from "@/lib/passkey";
import { api, idem, unwrap } from "../client";
import { qk } from "../keys";
import type { QueryOf, Schemas } from "../types";

export type AdminOverview = Schemas["AdminOverview"];
export type AdminAttentionItem = Schemas["AdminAttentionItem"];
export type AdminArea = Schemas["AdminArea"];
export type AdminDailySeries = Schemas["AdminDailySeries"];
export type AdminNode = Schemas["AdminNode"];
export type AdminNodeDetail = Schemas["AdminNodeDetail"];
export type AdminNodeList = Schemas["AdminNodeList"];
export type AdminNodeRole = Schemas["AdminNodeRole"];
export type AdminNodeStatus = Schemas["AdminNodeStatus"];
export type AdminResourceUse = Schemas["AdminResourceUse"];
export type AdminWorkload = Schemas["AdminWorkload"];
export type AdminWorkspace = Schemas["AdminWorkspace"];
export type AdminWorkspaceDetail = Schemas["AdminWorkspaceDetail"];
export type AdminEnvironmentQuota = Schemas["AdminEnvironmentQuota"];
export type AdminEnvironmentQuotaUpdate = Schemas["AdminEnvironmentQuotaUpdate"];
export type AdminWorkspaceFlag = Schemas["AdminWorkspaceFlag"];
export type AdminWorkspaceStatus = Schemas["AdminWorkspaceStatus"];
export type AdminUser = Schemas["AdminUser"];
export type AdminUserDetail = Schemas["AdminUserDetail"];
export type AdminFinance = Schemas["AdminFinance"];
export type AdminCostLine = Schemas["AdminCostLine"];
export type AdminInvoice = Schemas["AdminInvoice"];
export type AdminOperation = Schemas["AdminOperation"];
export type AdminOperationList = Schemas["AdminOperationList"];
export type AdminRef = Schemas["AdminRef"];
export type WaitlistEntry = Schemas["WaitlistEntry"];
export type Invite = Schemas["Invite"];
export type InviteCreated = Schemas["InviteCreated"];
export type InviteCreateRequest = Schemas["InviteCreateRequest"];
export type Change = Schemas["Change"];
export type KillSwitchStatus = Schemas["KillSwitchStatus"];
export type IncidentEvent = Schemas["IncidentEvent"];
export type IncidentSeverity = Schemas["IncidentSeverity"];
export type IncidentStatus = Schemas["IncidentStatus"];
export type IncidentUpdate = Schemas["IncidentUpdate"];

export type AdminWorkspaceFilter = QueryOf<"listAdminWorkspaces">;
export type AdminUserFilter = QueryOf<"listAdminUsers">;
export type AdminOperationFilter = QueryOf<"listAdminOperations">;
export type AdminNodeFilter = QueryOf<"listAdminNodes">;
export type IncidentFilter = QueryOf<"listIncidents">;
export type ChangeFilter = QueryOf<"listChanges">;

const POLL = 30_000;

/* Overview --------------------------------------------------------------- */

export function useAdminOverview() {
  return useQuery({
    queryKey: qk.admin.overview(),
    queryFn: () => unwrap(api.GET("/v1/admin/overview")),
    refetchInterval: POLL,
  });
}

/* Servers ---------------------------------------------------------------- */

export function useAdminNodes(filter: AdminNodeFilter = {}) {
  return useQuery({
    queryKey: qk.admin.nodes(filter),
    queryFn: () => unwrap(api.GET("/v1/admin/nodes", { params: { query: filter } })),
    refetchInterval: POLL,
  });
}

export function useAdminNode(node: string) {
  return useQuery({
    queryKey: qk.admin.node(node),
    queryFn: () => unwrap(api.GET("/v1/admin/nodes/{node}", { params: { path: { node } } })),
    enabled: !!node,
    refetchInterval: POLL,
  });
}

function useInvalidateAdmin() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: qk.admin.all() });
}

export function useCordonNode() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ node, cordon, reason }: { node: string; cordon: boolean; reason?: string }) =>
      unwrap(
        cordon
          ? api.POST("/v1/admin/nodes/{node}/cordon", { params: { path: { node }, header: idem() }, body: { reason } })
          : api.POST("/v1/admin/nodes/{node}/uncordon", { params: { path: { node }, header: idem() }, body: { reason } }),
      ),
    onSuccess: done,
  });
}

/**
 * Admin actions that move people's workloads or lock them out need a fresh passkey, bound to the target.
 * Returns the step_up body field.
 */
async function adminStepUp(subject: string): Promise<Schemas["PasskeyAssertion"]> {
  const ch = await unwrap(api.POST("/v1/auth/step-up/begin", { params: { header: idem() }, body: { purpose: "admin_action", subject_id: subject } }));
  const credential = await getAssertion(ch.public_key_request);
  return { challenge_id: ch.challenge_id, credential };
}

export function useDrainNode() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: async ({ node, reason, dryRun }: { node: string; reason: string; dryRun?: boolean }) => {
      const step_up = dryRun ? undefined : await adminStepUp(node);
      return unwrap(
        api.POST("/v1/admin/nodes/{node}/drain", {
          params: { path: { node }, header: idem(), query: dryRun ? { dry_run: true } : {} },
          body: { reason, step_up, required_spare: 1, timeout_seconds: 300 },
        }),
      );
    },
    onSuccess: (_d, v) => {
      if (!v.dryRun) void done();
    },
  });
}

/* Tenants ---------------------------------------------------------------- */

export function useAdminWorkspaces(filter: AdminWorkspaceFilter = {}) {
  return useQuery({
    queryKey: qk.admin.workspaces(filter),
    queryFn: () => unwrap(api.GET("/v1/admin/workspaces", { params: { query: filter } })),
    placeholderData: keepPreviousData,
  });
}

export function useAdminWorkspace(workspaceId: string) {
  return useQuery({
    queryKey: qk.admin.workspace(workspaceId),
    queryFn: () => unwrap(api.GET("/v1/admin/workspaces/{workspace_id}", { params: { path: { workspace_id: workspaceId } } })),
    enabled: !!workspaceId,
  });
}

export function useSuspendWorkspace() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: async ({ workspaceId, suspend, reason, notify = true }: { workspaceId: string; suspend: boolean; reason: string; notify?: boolean }) => {
      if (!suspend) {
        return unwrap(api.POST("/v1/admin/workspaces/{workspace_id}/unsuspend", { params: { path: { workspace_id: workspaceId }, header: idem() }, body: { reason } }));
      }
      const step_up = await adminStepUp(workspaceId);
      return unwrap(api.POST("/v1/admin/workspaces/{workspace_id}/suspend", { params: { path: { workspace_id: workspaceId }, header: idem() }, body: { reason, notify, step_up } }));
    },
    onSuccess: done,
  });
}

export function useAdminEnvironmentQuota(environmentId: string) {
  return useQuery({
    queryKey: qk.admin.environmentQuota(environmentId),
    queryFn: () => unwrap(api.GET("/v1/admin/environments/{environment_id}/quota", { params: { path: { environment_id: environmentId } } })),
    enabled: !!environmentId,
  });
}

export function useUpdateEnvironmentQuota() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ environmentId, body }: { environmentId: string; body: AdminEnvironmentQuotaUpdate }) =>
      unwrap(api.PATCH("/v1/admin/environments/{environment_id}/quota", { params: { path: { environment_id: environmentId }, header: idem() }, body })),
    onSuccess: done,
  });
}

/* Accounts --------------------------------------------------------------- */

export function useAdminUsers(filter: AdminUserFilter = {}) {
  return useQuery({
    queryKey: qk.admin.users(filter),
    queryFn: () => unwrap(api.GET("/v1/admin/users", { params: { query: filter } })),
    placeholderData: keepPreviousData,
  });
}

export function useAdminUser(userId: string) {
  return useQuery({
    queryKey: qk.admin.user(userId),
    queryFn: () => unwrap(api.GET("/v1/admin/users/{user_id}", { params: { path: { user_id: userId } } })),
    enabled: !!userId,
  });
}

export function useDisableUser() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: async ({ userId, disable, reason, notify = true }: { userId: string; disable: boolean; reason: string; notify?: boolean }) => {
      if (!disable) {
        return unwrap(api.POST("/v1/admin/users/{user_id}/enable", { params: { path: { user_id: userId }, header: idem() }, body: { reason } }));
      }
      const step_up = await adminStepUp(userId);
      return unwrap(api.POST("/v1/admin/users/{user_id}/disable", { params: { path: { user_id: userId }, header: idem() }, body: { reason, notify, step_up } }));
    },
    onSuccess: done,
  });
}

/* Money and usage -------------------------------------------------------- */

export function useAdminFinance(month?: string) {
  return useQuery({
    queryKey: qk.admin.finance(month ?? "current"),
    queryFn: () => unwrap(api.GET("/v1/admin/finance", { params: { query: month ? { month } : {} } })),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/* Operations ------------------------------------------------------------- */

export function useAdminOperations(filter: AdminOperationFilter = {}) {
  return useQuery({
    queryKey: qk.admin.operations(filter),
    queryFn: () => unwrap(api.GET("/v1/admin/operations", { params: { query: filter } })),
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });
}

/* Incidents -------------------------------------------------------------- */

export function useIncidents(filter: IncidentFilter = {}) {
  return useQuery({
    queryKey: qk.admin.incidents(filter),
    queryFn: () => unwrap(api.GET("/v1/incidents", { params: { query: filter } })),
    placeholderData: keepPreviousData,
    refetchInterval: POLL,
  });
}

export function useIncident(incidentId: string) {
  return useQuery({
    queryKey: qk.admin.incident(incidentId),
    queryFn: async () => {
      const [incident, events] = await Promise.all([
        unwrap(api.GET("/v1/incidents/{incident_id}", { params: { path: { incident_id: incidentId } } })),
        unwrap(api.GET("/v1/incidents/{incident_id}/events", { params: { path: { incident_id: incidentId } } })),
      ]);
      return { incident, events: events.data };
    },
    enabled: !!incidentId,
    refetchInterval: POLL,
  });
}

export function useUpdateIncident() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ incidentId, body }: { incidentId: string; body: IncidentUpdate }) =>
      unwrap(api.PATCH("/v1/incidents/{incident_id}", { params: { path: { incident_id: incidentId }, header: idem() }, body })),
    onSuccess: done,
  });
}

export function useAddIncidentNote() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ incidentId, message, kind = "note" }: { incidentId: string; message: string; kind?: "note" | "customer_notice" }) =>
      unwrap(api.POST("/v1/incidents/{incident_id}/events", { params: { path: { incident_id: incidentId }, header: idem() }, body: { kind, message } })),
    onSuccess: done,
  });
}

/* AI operations ---------------------------------------------------------- */

export function useChanges(filter: ChangeFilter = {}) {
  return useQuery({
    queryKey: qk.admin.changes(filter),
    queryFn: () => unwrap(api.GET("/v1/admin/changes", { params: { query: filter } })),
    placeholderData: keepPreviousData,
    refetchInterval: POLL,
  });
}

export function useAdminKillSwitch() {
  return useQuery({
    queryKey: ["kill-switch"],
    queryFn: () => unwrap(api.GET("/v1/admin/ai-ops/kill-switch")),
    refetchInterval: 15_000,
  });
}

export function useSetKillSwitch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ engage, reason }: { engage: boolean; reason: string }) => {
      if (engage) return unwrap(api.POST("/v1/admin/ai-ops/kill-switch/engage", { params: { header: idem() }, body: { reason } }));
      const ch = await unwrap(api.POST("/v1/auth/step-up/begin", { params: { header: idem() }, body: { purpose: "kill_switch" } }));
      const credential = await getAssertion(ch.public_key_request);
      return unwrap(api.POST("/v1/admin/ai-ops/kill-switch/disengage", { params: { header: idem() }, body: { reason, assertion: { challenge_id: ch.challenge_id, credential } } }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kill-switch"] });
      void qc.invalidateQueries({ queryKey: qk.admin.all() });
    },
  });
}

/* Signups ---------------------------------------------------------------- */

export function useWaitlist(status?: WaitlistEntry["status"]) {
  return useQuery({
    queryKey: qk.admin.waitlist(status ?? "all"),
    queryFn: () => unwrap(api.GET("/v1/admin/waitlist", { params: { query: { ...(status ? { status } : {}), limit: 200 } } })),
    placeholderData: keepPreviousData,
  });
}

export function useDeleteWaitlistEntry() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: (entryId: string) => unwrap(api.DELETE("/v1/admin/waitlist/{entry_id}", { params: { path: { entry_id: entryId }, header: idem() } })),
    onSuccess: done,
  });
}

export function useInvites() {
  return useQuery({
    queryKey: qk.admin.invites(),
    queryFn: () => unwrap(api.GET("/v1/admin/invites", { params: { query: { limit: 200 } } })),
  });
}

export function useCreateInvite() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: (body: InviteCreateRequest) => unwrap(api.POST("/v1/admin/invites", { params: { header: idem() }, body })),
    onSuccess: done,
  });
}

export function useRevokeInvite() {
  const done = useInvalidateAdmin();
  return useMutation({
    mutationFn: (inviteId: string) => unwrap(api.POST("/v1/admin/invites/{invite_id}/revoke", { params: { path: { invite_id: inviteId }, header: idem() } })),
    onSuccess: done,
  });
}

export type Passkey = Schemas["Passkey"];

/** The signed-in person's passkeys, each tagged with the host (relying party) it works on. */
export function useMyPasskeys(enabled = true) {
  return useQuery({ queryKey: qk.admin.passkeys(), queryFn: () => unwrap(api.GET("/v1/me/passkeys", { params: { query: { limit: 100 } } })), enabled });
}

/** Make a passkey for the host you are on. On admin.guv.dev that is the one admin step-ups need. */
export function useAddPasskey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const ch = await withAccountConfirmation("passkey:add", (headers) => unwrap(api.POST("/v1/auth/passkeys/register/begin", { headers, params: { header: idem() }, body: { name } })));
      const credential = await createCredential(ch.public_key_creation);
      return unwrap(api.POST("/v1/auth/passkeys/register/finish", { params: { header: idem() }, body: { challenge_id: ch.challenge_id, credential } }));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.admin.passkeys() }),
  });
}
